import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form';
import { ACL_TYPE } from '@openx/types/aclType';
import type { DealApi } from '@openx/types/deal';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators';
import { useTranslation } from 'react-i18next';
import type { DealDetailsSectionProps } from '../../../../types';
import { ThirdPartyFeeDetails } from './components/ThirdPartyFeeDetails';

export type CuratedDealFeesDetailsSectionProps = Pick<
  DealApi,
  'account_uid' | 'currency' | 'third_party_fees_config' | 'is_direct_deal'
>;

export const CuratedDealFeesDetailsSection = <T extends CuratedDealFeesDetailsSectionProps>({
  deal: { account_uid, currency, third_party_fees_config, is_direct_deal },
  historyMode,
}: Readonly<DealDetailsSectionProps<T>>) => {
  const { useIsAllowed } = usePermissionContext();
  const { isAllowed } = useIsAllowed();
  const { t } = useTranslation();

  if (!isAllowed(isAllowedByCompiledAcl(ACL_TYPE.THIRD_PARTY_FEES_READ))) {
    return null;
  }

  return (
    <FormSection
      divider
      historyMode={historyMode}
      data-test="openaudience-access"
      title={t('Curated Deal Fees')}
      rightColumnSize={RIGHT_COLUMN_WIDTH}
    >
      <FieldDetails
        toggle
        dataTest="enable-openaudience-access"
        title={t('Enable curation fees')}
        value={!!third_party_fees_config?.length}
      />

      {third_party_fees_config?.map(config => (
        <ThirdPartyFeeDetails
          accountUid={account_uid}
          currency={currency}
          grossCpmCap={config.gross_cpm_cap}
          grossShare={config.gross_share}
          historyMode={historyMode}
          isDirectDeal={!!is_direct_deal}
          key={config.partner_id}
          partnerId={config.partner_id}
          platformShare={config.platform_share}
          revenueMethod={config.revenue_method}
        />
      ))}
    </FormSection>
  );
};
